import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import useIntl from '@afosto/utils-react/intl/useIntl';
import Box from '@material-ui/core/Box';
import Card from '@material-ui/core/Card';
import CardHeader from '@material-ui/core/CardHeader';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import AdjustmentRow from '../AdjustmentRow';
import LoadingWrapper from '@afosto/components-react/LoadingWrapper';
import Select from '@afosto/app-components-react/Select';
import findObjectInArray from '@afosto/utils/array/findObjectInArray';
import formatCurrency from '@afosto/utils/formatCurrency';
import translations from './translations';
import ServiceCostRow from '../ServiceCostRow';

const formatCalculationSuggestion = calculation =>
  calculation
    ? {
        ...calculation,
        label: calculation?.number,
        value: calculation?.id,
      }
    : null;

const CostOverviewSection = ({ calculation, calculations, isLoading, projection }) => {
  const intl = useIntl();
  const formattedCalculationSuggestion = formatCalculationSuggestion(calculation);
  const projectionSuggestion = projection
    ? {
        ...projection,
        label: intl.formatMessage(translations.options.currentPrices),
        value: 'projection',
      }
    : null;
  const [selectedCalculation, setSelectedCalculation] = useState(
    formattedCalculationSuggestion ?? projectionSuggestion,
  );
  const calculationRef = useRef(formattedCalculationSuggestion);
  const projectionRef = useRef(projectionSuggestion);
  const { id: calculationId } = calculation ?? {};
  const { pricing_at: pricingAt } = projection ?? {};
  const currency = selectedCalculation?.currency ?? 'EUR';

  useEffect(() => {
    calculationRef.current = formattedCalculationSuggestion;
    projectionRef.current = projectionSuggestion;
  });

  useEffect(() => {
    setSelectedCalculation(calculationRef.current ?? projectionRef.current);
  }, [calculationId, pricingAt]);

  const handleChangeCalculation = (ev, newValue) => {
    if (newValue === 'projection') {
      setSelectedCalculation(projectionSuggestion);
      return;
    }

    const calculationResult = findObjectInArray(calculations, 'id', newValue);

    if (calculationResult) {
      setSelectedCalculation(formatCalculationSuggestion(calculationResult));
    } else {
      setSelectedCalculation(formattedCalculationSuggestion);
    }
  };

  const showSelection = calculations?.length > 1 || (calculations?.length >= 1 && projection);

  const shouldShowVatBelowTotal =
    (selectedCalculation?.is_including_vat && !selectedCalculation?.is_vat_shifted) ||
    (!selectedCalculation?.is_including_vat && selectedCalculation?.is_vat_shifted);
  const shouldShowVatAsNegative =
    selectedCalculation?.is_vat_shifted && selectedCalculation?.is_including_vat;

  return (
    <Card>
      <LoadingWrapper isLoading={isLoading}>
        <CardHeader
          title={intl.formatMessage(translations.title)}
          titleTypographyProps={{ variant: 'h6' }}
        />
        {showSelection && (
          <Box px={2} pb={1}>
            <Select
              name="calculation"
              value={selectedCalculation?.value}
              onChange={handleChangeCalculation}
              margin="dense"
              suggestions={[
                ...calculations.map(({ number, id }) => ({
                  label: number,
                  value: id,
                })),
                ...(projectionSuggestion ? [projectionSuggestion] : []),
              ]}
              register={false}
            />
          </Box>
        )}
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{intl.formatMessage(translations.overview.post)}</TableCell>
              <TableCell>{intl.formatMessage(translations.overview.amount)}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(selectedCalculation?.services ?? []).map(serviceCost => (
              <ServiceCostRow
                currency={currency}
                serviceCost={serviceCost}
                key={serviceCost.description}
              />
            ))}
            <TableRow>
              <TableCell>
                {intl.formatMessage(translations.overview.subtotal)}{' '}
                {selectedCalculation?.is_including_vat ? 'incl.' : 'excl.'}
              </TableCell>
              <TableCell>
                {formatCurrency((selectedCalculation?.subtotal ?? 0) / 100, currency)}
              </TableCell>
            </TableRow>
            {(selectedCalculation?.adjustments ?? []).map(adjustment => (
              <AdjustmentRow
                currency={currency}
                adjustment={adjustment}
                key={adjustment.reference}
              />
            ))}
            {shouldShowVatBelowTotal && (
              <>
                <TableRow>
                  <TableCell>{intl.formatMessage(translations.overview.total)}</TableCell>
                  <TableCell>
                    {formatCurrency((selectedCalculation?.total ?? 0) / 100, currency)}
                  </TableCell>
                </TableRow>
              </>
            )}
            {(selectedCalculation?.vat ?? []).map(vat => (
              <TableRow key={vat.rate}>
                <TableCell>
                  {shouldShowVatBelowTotal
                    ? `${
                        !selectedCalculation?.is_vat_shifted
                          ? intl.formatMessage(translations.overview.ofWhichVat)
                          : intl.formatMessage(translations.overview.vat)
                      } ${
                        selectedCalculation.is_vat_shifted
                          ? intl.formatMessage(translations.overview.shiftedVat)
                          : ''
                      } ${vat.rate}%`
                    : `${intl.formatMessage(translations.overview.vat)}
                      ${
                        selectedCalculation.is_vat_shifted
                          ? intl.formatMessage(translations.overview.shiftedVat)
                          : ''
                      } ${vat.rate}%`}
                </TableCell>
                <TableCell>
                  {formatCurrency(
                    (shouldShowVatAsNegative ? vat.amount * -1 : vat.amount) / 100,
                    currency,
                  )}
                </TableCell>
              </TableRow>
            ))}
            {!shouldShowVatBelowTotal && (
              <TableRow>
                <TableCell>{intl.formatMessage(translations.overview.total)}</TableCell>
                <TableCell>
                  {formatCurrency((selectedCalculation?.total ?? 0) / 100, currency)}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </LoadingWrapper>
    </Card>
  );
};

CostOverviewSection.propTypes = {
  calculation: PropTypes.object,
  calculations: PropTypes.array,
  isLoading: PropTypes.bool,
  projection: PropTypes.object,
};

CostOverviewSection.defaultProps = {
  calculation: null,
  calculations: [],
  isLoading: true,
  projection: null,
};

export default CostOverviewSection;
